import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../shared/context/ThemeContext";
import { OnboardingStep } from "../types";

interface OnboardingLayoutProps {
  children: ReactNode;
  currentStep: OnboardingStep;
}

/**
 * Layout component for onboarding pages
 */
const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  children,
  currentStep,
}) => {
  const { mode, toggleMode } = useTheme();

  return (
    <main className="bg-white dark:bg-gray-900">
      <div className="relative flex">
        {/* Content */}
        <div className="w-full md:w-1/2">
          <div className="min-h-[100dvh] h-full flex flex-col after:flex-1">
            <div className="flex-1">
              {/* Header */}
              <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                {/* Logo */}
                <Link className="block" to="/">
                  <img
                    src="/assets/images/logo.svg"
                    alt="GCCA Logo"
                    className="h-14 object-contain"
                  />
                </Link>
                {/* <div className="text-sm">
                  <button
                    className={`w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full ${theme.text.primary}`}
                    onClick={toggleMode}
                  >
                    {mode === 'light' ? (
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                        <path d="M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                        <path d="M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0zm0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13zm8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5zM3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8zm10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0zm-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0zm9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707zM4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708z"/>
                      </svg>
                    )}
                  </button>
                </div> */}
              </div>

              {/* Progress bar */}
              <div className="px-4 pt-12 pb-8">
                <div className="max-w-md mx-auto w-full">
                  <div className="relative">
                    <div
                      className="absolute left-0 top-1/2 -mt-px w-full h-0.5 bg-gray-200 dark:bg-gray-700/60"
                      aria-hidden="true"
                    ></div>
                    <ul className="relative flex justify-between w-full">
                      <li>
                        <Link
                          className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            currentStep >= OnboardingStep.ORGANIZATION_TYPE
                              ? "bg-blue-500 text-white"
                              : "bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400"
                          }`}
                          to="/onboarding-1"
                        >
                          1
                        </Link>
                      </li>
                      <li>
                        <Link
                          className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            currentStep >= OnboardingStep.ORGANIZATION_INFO
                              ? "bg-blue-500 text-white"
                              : "bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400"
                          }`}
                          to="/onboarding-2"
                        >
                          2
                        </Link>
                      </li>
                      <li>
                        <Link
                          className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            currentStep >= OnboardingStep.PROFILE_INFO
                              ? "bg-blue-500 text-white"
                              : "bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400"
                          }`}
                          to="/onboarding-3"
                        >
                          3
                        </Link>
                      </li>
                      <li>
                        <Link
                          className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            currentStep >= OnboardingStep.COMPLETE
                              ? "bg-blue-500 text-white"
                              : "bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400"
                          }`}
                          to="/onboarding-4"
                        >
                          4
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="px-4 py-8">
              <div className="max-w-md mx-auto">{children}</div>
            </div>
          </div>
        </div>

        {/* Image */}
        <div
          className="hidden md:block absolute top-0 bottom-0 right-0 md:w-1/2"
          aria-hidden="true"
        >
          <img
            className="object-cover object-center w-full h-full"
            src="/assets/images/sign-graphic.jpg"
            width="760"
            height="1024"
            alt="Onboarding"
          />
        </div>
      </div>
    </main>
  );
};

export default OnboardingLayout;
